import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, ArrowLeft, Calendar, DollarSign, AlertTriangle,
  RefreshCw, Loader2, ArrowUpRight, ArrowDownRight, Wallet, PiggyBank
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend, ReferenceLine
} from "recharts";

const CHART_COLORS = {
  income: "hsl(158, 64%, 40%)",
  expense: "hsl(4, 74%, 55%)",
  recurring: "hsl(195, 72%, 36%)",
  forecast: "hsl(38, 92%, 50%)",
  net: "hsl(200, 90%, 42%)"
};

export default function CashFlowPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [forecast, setForecast] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [forecastMonths, setForecastMonths] = useState("3");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/cash-flow/forecast?months=${forecastMonths}`);
      setForecast(response.data);
    } catch (error) {
      console.error("Error fetching forecast:", error);
      toast.error(t("failed_load_data", "dashboard") || "Failed to load data");
    } finally {
      setIsLoading(false);
    }
  }, [forecastMonths, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-card border rounded-lg shadow-lg p-3">
        <p className="font-medium text-sm mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="max-w-[1400px] mx-auto px-4 py-4">
            <Skeleton className="h-8 w-48" />
          </div>
        </header>
        <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}
          </div>
          <Skeleton className="h-[400px]" />
        </main>
      </div>
    );
  }

  const netPositive = (forecast?.net_cash_flow || 0) >= 0;

  return (
    <div className="min-h-screen bg-background" data-testid="cash-flow-page">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold text-lg flex items-center gap-2">
                <Wallet className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
                {t("cash_flow_forecast", "company") || "Cash Flow Forecast"}
              </h1>
              <p className="text-xs text-muted-foreground">
                {t("project_future_cash", "company") || "Project future cash flow based on historical data"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={forecastMonths} onValueChange={setForecastMonths}>
              <SelectTrigger className="w-[150px]" data-testid="period-select">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 Month</SelectItem>
                <SelectItem value="3">3 Months</SelectItem>
                <SelectItem value="6">6 Months</SelectItem>
                <SelectItem value="12">12 Months</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={fetchData} data-testid="refresh-button">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
        {/* Alerts */}
        {forecast?.alerts?.length > 0 && (
          <div className="space-y-2">
            {forecast.alerts.map((alert, index) => (
              <Alert key={index} variant={alert.includes("Warning") ? "destructive" : "default"}>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Alert</AlertTitle>
                <AlertDescription>{alert}</AlertDescription>
              </Alert>
            ))}
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="kpi-opening">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <PiggyBank className="h-4 w-4" />
                <span className="text-sm">Opening Balance</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(forecast?.opening_balance || 0)}</p>
              <p className="text-xs text-muted-foreground">Current position</p>
            </CardContent>
          </Card>

          <Card data-testid="kpi-income">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <ArrowUpRight className="h-4 w-4" />
                <span className="text-sm">Expected Income</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(forecast?.total_income || 0)}</p>
              <p className="text-xs text-muted-foreground">Including {formatCurrency(forecast?.recurring_income || 0)} recurring</p>
            </CardContent>
          </Card>

          <Card data-testid="kpi-expenses">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-red-600 mb-1">
                <ArrowDownRight className="h-4 w-4" />
                <span className="text-sm">Expected Expenses</span>
              </div>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(forecast?.total_expenses || 0)}</p>
              <p className="text-xs text-muted-foreground">Based on historical avg</p>
            </CardContent>
          </Card>

          <Card data-testid="kpi-net">
            <CardContent className="p-4">
              <div className={`flex items-center gap-2 ${netPositive ? 'text-green-600' : 'text-red-600'} mb-1`}>
                {netPositive ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                <span className="text-sm">Net Cash Flow</span>
              </div>
              <p className={`text-2xl font-bold ${netPositive ? 'text-green-600' : 'text-red-600'}`}>
                {netPositive ? '+' : ''}{formatCurrency(forecast?.net_cash_flow || 0)}
              </p>
              <p className="text-xs text-muted-foreground">Projected closing: {formatCurrency(forecast?.closing_balance || 0)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Forecast Accuracy */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Forecast Confidence</span>
              <Badge variant={forecast?.forecast_accuracy >= 70 ? "default" : forecast?.forecast_accuracy >= 40 ? "secondary" : "outline"}>
                {forecast?.forecast_accuracy >= 70 ? "High" : forecast?.forecast_accuracy >= 40 ? "Medium" : "Low"}
              </Badge>
            </div>
            <Progress value={forecast?.forecast_accuracy || 0} className="h-2" />
            <p className="text-xs text-muted-foreground mt-2">
              Based on {forecast?.forecast_accuracy >= 70 ? "extensive" : forecast?.forecast_accuracy >= 40 ? "moderate" : "limited"} historical data. 
              Add more transactions to improve accuracy.
            </p>
          </CardContent>
        </Card>

        {/* Weekly Summary Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Weekly Cash Flow Projection</CardTitle>
            <CardDescription>Expected income vs expenses by week</CardDescription>
          </CardHeader>
          <CardContent>
            {!forecast?.weekly_summary?.length ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Not enough data for weekly projections
              </div>
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={forecast.weekly_summary} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--ff-border))" />
                    <XAxis 
                      dataKey="week" 
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                      tickFormatter={(v) => `Week ${v}`}
                    />
                    <YAxis 
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                      tickFormatter={(v) => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="expected_income" name="Income" fill={CHART_COLORS.income} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expected_expenses" name="Expenses" fill={CHART_COLORS.expense} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Net Flow Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Net Cash Flow by Week</CardTitle>
            <CardDescription>Income minus expenses projection</CardDescription>
          </CardHeader>
          <CardContent>
            {!forecast?.weekly_summary?.length ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                Not enough data for net flow projections
              </div>
            ) : (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={forecast.weekly_summary} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="netGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={CHART_COLORS.net} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={CHART_COLORS.net} stopOpacity={0.02}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--ff-border))" />
                    <XAxis 
                      dataKey="week" 
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                      tickFormatter={(v) => `Week ${v}`}
                    />
                    <YAxis 
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                      tickFormatter={(v) => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                    <Area 
                      type="monotone" 
                      dataKey="net_flow" 
                      name="Net Flow"
                      stroke={CHART_COLORS.net} 
                      fill="url(#netGradient)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recurring Revenue Schedule */}
        {forecast?.daily_breakdown?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Upcoming Recurring Revenue</CardTitle>
              <CardDescription>Scheduled billing dates</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {forecast.daily_breakdown.slice(0, 20).map((entry, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-[hsl(var(--ff-primary)/.1)]">
                        <RefreshCw className="h-4 w-4 text-[hsl(var(--ff-primary))]" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{entry.description}</p>
                        <p className="text-xs text-muted-foreground">{entry.date}</p>
                      </div>
                    </div>
                    <p className="font-bold text-green-600">+{formatCurrency(entry.amount)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
